import type {
  FieldErrorMap,
  FieldName,
  WalkinActivity,
  WalkinCreateLeadBody,
  WalkinLeadLinkResult,
  WalkinLeadMatch,
  WalkinLinkLeadBody,
  WalkinLookupMatch,
  WalkinLookupResult,
  WalkinPostBody,
  WalkinSameDayWalkin,
  WalkinSubmitResult,
} from './types';

const FIELD_NAMES: readonly FieldName[] = [
  'clinicId',
  'patientName',
  'phoneRaw',
  'patientAge',
  'source',
  'sourceDetail',
  'sourceConfidence',
  'patientIsNew',
  'referringDoctorName',
  'selectedContactId',
];

function isFieldName(value: unknown): value is FieldName {
  return (
    typeof value === 'string'
    && (FIELD_NAMES as readonly string[]).includes(value)
  );
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

/**
 * Resolve the backend base URL.
 *
 * **Local dev:** when `VITE_USE_RELATIVE_API=true`, returns `''` so requests go to
 * same-origin `/api/*` and Vite's dev server proxies to the webhooks process (no CORS).
 *
 * **Production / preview:** reads `VITE_API_BASE_URL` or defaults to Phase 1 origin.
 */
export function getApiBaseUrl(): string {
  const useRelative =
    import.meta.env.DEV && import.meta.env.VITE_USE_RELATIVE_API === 'true';
  if (useRelative) {
    return '';
  }

  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  const base = typeof fromEnv === 'string' && fromEnv.length > 0
    ? fromEnv
    : 'https://app.adoracare.ai';
  return trimTrailingSlash(base);
}

interface ServerFieldError {
  field: unknown;
  message: unknown;
}

function extractData(body: unknown): Record<string, unknown> {
  if (body === null || typeof body !== 'object') return {};
  const maybe = (body as { data?: unknown }).data;
  if (maybe === null || typeof maybe !== 'object') return {};
  return maybe as Record<string, unknown>;
}

function mapResponse(status: number, body: unknown): WalkinSubmitResult {
  const data = extractData(body);

  if (status === 201) {
    const patientName =
      typeof data.patient_name === 'string' ? data.patient_name : '';
    const walkinId = data.walkin_id != null ? String(data.walkin_id) : '';
    return { kind: 'success', patientName, walkinId };
  }

  if (status === 400) {
    if (data.code === 'validation_failed') {
      const fieldErrors: FieldErrorMap = {};
      const orphans: string[] = [];
      const rawFields = data.fields;
      if (Array.isArray(rawFields)) {
        for (const raw of rawFields as unknown[]) {
          if (raw === null || typeof raw !== 'object') continue;
          const f = raw as ServerFieldError;
          const message =
            typeof f.message === 'string' && f.message.length > 0
              ? f.message
              : 'Invalid value';
          if (isFieldName(f.field)) {
            fieldErrors[f.field] = message;
          } else if (typeof f.field === 'string') {
            orphans.push(`${f.field}: ${message}`);
          }
        }
      }
      return { kind: 'validation', fieldErrors, orphans };
    }
    if (data.error === 'invalid_json') {
      return {
        kind: 'badRequest',
        message: 'Invalid form data, please try again',
      };
    }
    return { kind: 'badRequest', message: 'Bad request, please try again' };
  }

  if (status === 401) {
    return { kind: 'auth' };
  }

  // v1 spine (migration 20260520_2) removed the duplicate_phone
  // short-circuit: contacts are now keyed on (organization_id,
  // phone_fingerprint) and re-using an existing contact returns
  // 201 with action='duplicate_same_person'. The frontend treats
  // that as a normal success — no separate 'duplicate' kind.

  if (status >= 500) {
    return { kind: 'serverError' };
  }

  return { kind: 'unknown' };
}

/**
 * Submit a walk-in. The cross-origin POST is intentionally
 * cookie-less (no `credentials: 'include'`); auth flows through
 * the X-Walkin-Form-Secret header — same as public/walkin.html.
 */
export async function submitWalkin(
  body: WalkinPostBody,
  secret: string,
): Promise<WalkinSubmitResult> {
  const url = `${getApiBaseUrl()}/api/walkins/create`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type':         'application/json',
        'X-Walkin-Form-Secret': secret,
      },
      body: JSON.stringify(body),
    });
  } catch {
    return { kind: 'network' };
  }

  let parsed: unknown = null;
  try {
    parsed = await resp.json();
  } catch {
    parsed = null;
  }

  return mapResponse(resp.status, parsed);
}

// ─── Walk-in access-code pre-flight (Walk-in UX Hardening) ────────

export interface WalkinValidateResult {
  kind:
    | 'ok'
    | 'invalid'
    | 'rateLimited'
    | 'serviceUnavailable'
    | 'network'
    | 'unknown';
  retryAfterSeconds?: number;
}

/**
 * Validate the X-Walkin-Form-Secret before the receptionist proceeds
 * past SecretScreen. The endpoint returns 204 on a valid secret, 401
 * on invalid, 503 if the env is unconfigured, and 429 when rate
 * limited. No body is sent or returned beyond the status code.
 *
 * Used to guarantee that an invalid access code is never written to
 * sessionStorage — SecretScreen calls this before any session.ts
 * write, and only persists/transitions on `kind === 'ok'`.
 */
export async function validateAccessSecret(
  secret: string,
): Promise<WalkinValidateResult> {
  const url = `${getApiBaseUrl()}/api/walkins/validate-access`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type':         'application/json',
        'X-Walkin-Form-Secret': secret,
      },
    });
  } catch {
    return { kind: 'network' };
  }

  if (resp.status === 204) return { kind: 'ok' };
  if (resp.status === 401) return { kind: 'invalid' };
  if (resp.status === 503) return { kind: 'serviceUnavailable' };
  if (resp.status === 429) {
    const retryHeader = resp.headers.get('Retry-After');
    const parsed = retryHeader ? parseInt(retryHeader, 10) : NaN;
    const retryAfterSeconds =
      Number.isFinite(parsed) && parsed > 0 ? parsed : 60;
    return { kind: 'rateLimited', retryAfterSeconds };
  }
  return { kind: 'unknown' };
}

// ─── Walk-in patient lookup (Sprint 6 phone-first wiring) ─────────

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asPositiveNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && value.length > 0) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/**
 * Defensive parser for one match row from the server. Drops a
 * row entirely if `contactId` or `clinicId` cannot be coerced to
 * positive numbers — those are required for downstream UI logic.
 * Other admin fields fall back to neutral defaults so a partially
 * malformed row is still rendered safely.
 *
 * Never extracts or returns phone_fingerprint, raw phone, encrypted
 * phone, or any clinical field — even if the server were to leak
 * them (defense in depth).
 */
function parseLookupMatch(raw: unknown): WalkinLookupMatch | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const contactId = asPositiveNumber(r.contactId);
  if (contactId === null) return null;
  // clinicId is nullable in the v1 spine — contacts can exist in
  // an org before being resolved to a specific clinic. Coerce
  // explicit null / undefined to null; reject non-numeric strings.
  let clinicId: number | null = null;
  if (r.clinicId !== null && r.clinicId !== undefined) {
    const parsed = asPositiveNumber(r.clinicId);
    if (parsed === null) return null;
    clinicId = parsed;
  }
  const ageVal = r.age;
  const age =
    typeof ageVal === 'number' && Number.isFinite(ageVal)
      ? ageVal
      : null;
  const clinicNameRaw = r.clinicName;
  const clinicName =
    typeof clinicNameRaw === 'string' && clinicNameRaw.length > 0
      ? clinicNameRaw
      : null;
  return {
    contactId,
    patientName: asString(r.patientName),
    age,
    clinicId,
    clinicName,
    sameClinic: r.sameClinic === true,
  };
}

function parseLookupMatches(raw: unknown): WalkinLookupMatch[] {
  if (!Array.isArray(raw)) return [];
  const out: WalkinLookupMatch[] = [];
  for (const entry of raw as unknown[]) {
    const parsed = parseLookupMatch(entry);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

/**
 * Defensive parser for one lead match (S2-P5A2). Drops a row whose
 * `leadId` is not a positive number. Only named admin-plane fields are
 * read; phone, fingerprint, and any clinical field are never extracted
 * even if the server were to leak them (defense in depth, mirroring
 * parseLookupMatch).
 */
function parseLeadMatch(raw: unknown): WalkinLeadMatch | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const leadId = asPositiveNumber(r.leadId);
  if (leadId === null) return null;
  let clinicId: number | null = null;
  if (r.clinicId !== null && r.clinicId !== undefined) {
    const parsed = asPositiveNumber(r.clinicId);
    if (parsed === null) return null;
    clinicId = parsed;
  }
  const clinicNameRaw = r.clinicName;
  const clinicName =
    typeof clinicNameRaw === 'string' && clinicNameRaw.length > 0
      ? clinicNameRaw
      : null;
  const createdAtRaw = r.createdAt;
  const createdAt =
    typeof createdAtRaw === 'string' && createdAtRaw.length > 0
      ? createdAtRaw
      : null;
  return {
    leadId,
    leadName: asString(r.leadName),
    source: asString(r.source),
    caseStatus: asString(r.caseStatus, 'Open'),
    createdAt,
    clinicId,
    clinicName,
    sameClinic: r.sameClinic === true,
  };
}

function parseLeadMatches(raw: unknown): WalkinLeadMatch[] {
  if (!Array.isArray(raw)) return [];
  const out: WalkinLeadMatch[] = [];
  for (const entry of raw as unknown[]) {
    const parsed = parseLeadMatch(entry);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function asCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.floor(value);
  }
  return 0;
}

/**
 * Parse the prior-activity block (S2-P5A2). Returns null when absent or
 * malformed. Reads only the three count/timestamp fields; nothing else.
 */
function parseActivity(raw: unknown): WalkinActivity | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const lastActivityAt =
    typeof r.lastActivityAt === 'string' && r.lastActivityAt.length > 0
      ? r.lastActivityAt
      : null;
  return {
    walkinCount: asCount(r.walkinCount),
    checkinCount: asCount(r.checkinCount),
    lastActivityAt,
  };
}

/**
 * Parse the GUARD 1 same-day-walkin block (S2-P5E-B2). Returns null when absent
 * or when the timestamp is missing/empty, so an old/degraded response safely
 * means "no same-day duplicate signal". Reads only the timestamp - never a
 * phone, fingerprint, or clinical field.
 */
function parseSameDayWalkin(raw: unknown): WalkinSameDayWalkin | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const walkedInAt =
    typeof r.walkedInAt === 'string' && r.walkedInAt.length > 0
      ? r.walkedInAt
      : null;
  if (walkedInAt === null) return null;
  return { walkedInAt };
}

export function mapLookupResponse(
  status: number,
  body: unknown,
): WalkinLookupResult {
  const data = extractData(body);

  if (status === 200 && data.ok === true) {
    const truncated = data.truncated === true;
    const matches = parseLookupMatches(data.matches);
    // Mirror server's `found`; fall back to the array length if
    // the server omits it for any reason.
    const found =
      data.found === true ||
      (typeof data.found !== 'boolean' && matches.length > 0);
    // S2-P5A2 additive blocks. Absent/old responses parse to [] / null,
    // so the field is always present and safe for the caller.
    const leadMatches = parseLeadMatches(data.leadMatches);
    const leadMatchesTruncated = data.leadMatchesTruncated === true;
    const activity = parseActivity(data.activity);
    const sameDayWalkin = parseSameDayWalkin(data.sameDayWalkin);
    return {
      kind: 'success',
      found,
      truncated,
      matches,
      leadMatches,
      leadMatchesTruncated,
      activity,
      sameDayWalkin,
    };
  }

  if (status === 400 && data.code === 'validation_failed') {
    const fieldErrors: FieldErrorMap = {};
    const orphans: string[] = [];
    const rawFields = data.fields;
    if (Array.isArray(rawFields)) {
      for (const raw of rawFields as unknown[]) {
        if (raw === null || typeof raw !== 'object') continue;
        const f = raw as ServerFieldError;
        const message =
          typeof f.message === 'string' && f.message.length > 0
            ? f.message
            : 'Invalid value';
        if (isFieldName(f.field)) {
          fieldErrors[f.field] = message;
        } else if (typeof f.field === 'string') {
          orphans.push(`${f.field}: ${message}`);
        }
      }
    }
    return { kind: 'validation', fieldErrors, orphans };
  }

  if (status === 401) {
    return { kind: 'auth' };
  }

  // 404 → clinic_not_found. The receptionist just selected this
  // clinic from the dropdown, so the only realistic cause is a
  // backend/data inconsistency. Surface as a generic server error
  // rather than a special kind so the UI message stays simple.
  if (status === 404) {
    return { kind: 'serverError' };
  }

  if (status === 429) {
    const message =
      typeof data.message === 'string' && data.message.length > 0
        ? data.message
        : 'Too many requests. Wait a moment and try again.';
    return { kind: 'rateLimited', message };
  }

  if (status >= 500) {
    return { kind: 'serverError' };
  }

  return { kind: 'unknown' };
}

/**
 * Look up an existing patient contact by clinic + phone. Org-
 * scoped on the backend (returns matches across same-org sibling
 * clinics) and capped at 5 matches with a `truncated` flag. The
 * cross-origin POST is intentionally cookie-less; auth flows
 * through the X-Walkin-Form-Secret header — same as submitWalkin.
 */
export async function lookupWalkinPatient(
  body: { clinicId: number; phoneRaw: string },
  secret: string,
): Promise<WalkinLookupResult> {
  const url = `${getApiBaseUrl()}/api/walkins/lookup`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type':         'application/json',
        'X-Walkin-Form-Secret': secret,
      },
      body: JSON.stringify(body),
    });
  } catch {
    return { kind: 'network' };
  }

  let parsed: unknown = null;
  try {
    parsed = await resp.json();
  } catch {
    parsed = null;
  }

  return mapLookupResponse(resp.status, parsed);
}

// --- Walk-in -> CRM lead create/link (S2-P5A3-BUILD-01) -----------
//
// Two write calls gated client-side by WALKIN_LEAD_LINK_V1_ENABLED (the
// caller only invokes these when the flag is on) and server-side by the
// 503 walkin_lead_link_disabled gate. Same cookie-less,
// X-Walkin-Form-Secret-header auth as submitWalkin. On success the result
// carries leadId so the caller can navigate to the lead's Lead 360.

/** Shared status -> result mapper for create-lead / link-lead responses. */
export function mapLeadLinkResponse(
  status: number,
  body: unknown,
): WalkinLeadLinkResult {
  // Server replies are wrapped by sendJson as { success, data }; the result
  // / error payload lives under `data` (same convention as mapResponse).
  const data = extractData(body);

  if (status === 200 || status === 201) {
    const leadId = asPositiveNumber(data.leadId ?? data.lead_id);
    const action = data.action === 'linked' ? 'linked' : 'created';
    if (leadId !== null) {
      return { kind: 'success', action, leadId };
    }
    return { kind: 'unknown' };
  }

  if (status === 400) {
    if (data.code === 'validation_failed') {
      const fieldErrors: FieldErrorMap = {};
      const orphans: string[] = [];
      const rawFields = data.fields as unknown;
      if (Array.isArray(rawFields)) {
        for (const raw of rawFields as unknown[]) {
          if (raw === null || typeof raw !== 'object') continue;
          const f = raw as ServerFieldError;
          const message =
            typeof f.message === 'string' && f.message.length > 0
              ? f.message
              : 'Invalid value';
          if (isFieldName(f.field)) {
            fieldErrors[f.field] = message;
          } else if (typeof f.field === 'string') {
            orphans.push(`${f.field}: ${message}`);
          }
        }
      }
      return { kind: 'validation', fieldErrors, orphans };
    }
    return { kind: 'unknown' };
  }

  if (status === 401) return { kind: 'auth' };
  if (status === 404) {
    return {
      kind: 'notFound',
      message: 'That lead could not be found at this clinic.',
    };
  }
  if (status === 409) {
    return {
      kind: 'conflict',
      message:
        'This visit could not be linked (it may already be linked, or the lead belongs to another clinic).',
    };
  }
  if (status === 503) {
    if (data.error === 'walkin_lead_link_disabled') return { kind: 'disabled' };
    return { kind: 'serverError' };
  }
  if (status === 429) {
    return {
      kind: 'rateLimited',
      message: 'Too many requests. Please wait a minute and try again.',
    };
  }
  if (status >= 500) return { kind: 'serverError' };
  return { kind: 'unknown' };
}

async function postLeadLink(
  pathSuffix: 'create-lead' | 'link-lead',
  body: WalkinCreateLeadBody | WalkinLinkLeadBody,
  secret: string,
): Promise<WalkinLeadLinkResult> {
  const url = `${getApiBaseUrl()}/api/walkins/${pathSuffix}`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Walkin-Form-Secret': secret,
      },
      body: JSON.stringify(body),
    });
  } catch {
    return { kind: 'network' };
  }

  let parsed: unknown = null;
  try {
    parsed = await resp.json();
  } catch {
    parsed = null;
  }

  return mapLeadLinkResponse(resp.status, parsed);
}

/**
 * Register the walk-in AND create a new walk_in lead linked to it. On
 * success the lead's id is returned so the caller can open its Lead 360.
 */
export async function createWalkinLead(
  body: WalkinCreateLeadBody,
  secret: string,
): Promise<WalkinLeadLinkResult> {
  return postLeadLink('create-lead', body, secret);
}

/**
 * Register the walk-in AND link it to an EXISTING lead the receptionist
 * confirmed. On success the lead's id is returned for Lead 360 navigation.
 */
export async function linkWalkinLead(
  body: WalkinLinkLeadBody,
  secret: string,
): Promise<WalkinLeadLinkResult> {
  return postLeadLink('link-lead', body, secret);
}
